import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TooltipBodyProps } from "@design-system/Tooltip/Tooltip.types";
import type { AvatarGroupSize } from "./avatarGroupContext";
import styles from "./AvatarOverflowChip.module.scss";

export interface AvatarOverflowChipProps {
    /** Names of the hidden items, read out (and listed in the default tooltip). */
    hiddenLabels: readonly string[];
    /** Items the stack does not list, added to the count. @default 0 */
    extraCount?: number;
    size: AvatarGroupSize;
    /** Replaces the default "N more" tooltip that lists `hiddenLabels`. */
    tooltip?: TooltipBodyProps;
}

/**
 * The +N chip after a stack of avatars or logos. It lists the hidden items in
 * a tooltip on hover (or shows `tooltip`), is hidden from assistive tech and
 * replaced by visually hidden "and N more: ..." text. Renders nothing when
 * nothing is hidden.
 */
export const AvatarOverflowChip = ({
    hiddenLabels,
    extraCount = 0,
    size,
    tooltip,
}: Readonly<AvatarOverflowChipProps>) => {
    const hiddenCount = hiddenLabels.length + extraCount;
    if (hiddenCount === 0) {
        return null;
    }
    const bullets =
        extraCount > 0 && hiddenLabels.length > 0
            ? [...hiddenLabels, `and ${extraCount} more`]
            : [...hiddenLabels];
    const hiddenText =
        hiddenLabels.length > 0
            ? `and ${hiddenCount} more: ${bullets.join(", ")}`
            : `and ${extraCount} more`;

    return (
        <span className={styles.overflow}>
            <Tooltip
                {...(tooltip ?? { content: `${hiddenCount} more`, bullets })}
            >
                <span aria-hidden className={styles.chip}>
                    <Tag size={size} numeric color="primary">
                        +{hiddenCount}
                    </Tag>
                </span>
            </Tooltip>
            <span className={styles.hiddenText}>{hiddenText}</span>
        </span>
    );
};
